import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];
const DAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

interface DatePickerProps {
  value: string; // "YYYY-MM-DD" or ""
  onChange: (date: string) => void;
  className?: string;
  min?: string; // "YYYY-MM-DD"
  placeholder?: string;
}

function parseDate(str: string): { year: number; month: number; day: number } | null {
  if (!str) return null;
  const parts = str.split('-');
  if (parts.length !== 3) return null;
  return { year: parseInt(parts[0]), month: parseInt(parts[1]) - 1, day: parseInt(parts[2]) };
}

function formatDisplay(str: string): string {
  const d = parseDate(str);
  if (!d) return '';
  return `${MONTHS[d.month].slice(0, 3)} ${d.day}, ${d.year}`;
}

function daysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

// Monday-first: returns 0=Mon ... 6=Sun
function firstWeekday(year: number, month: number): number {
  const day = new Date(year, month, 1).getDay(); // 0=Sun
  return day === 0 ? 6 : day - 1;
}

function toDateKey(year: number, month: number, day: number): string {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

const CALENDAR_WIDTH = 240;
const CALENDAR_HEIGHT = 260; // approximate

const DatePicker: React.FC<DatePickerProps> = ({ value, onChange, className = '', min, placeholder = 'Pick a date' }) => {
  const today = new Date();
  const todayKey = toDateKey(today.getFullYear(), today.getMonth(), today.getDate());

  const selected = parseDate(value);
  const [open, setOpen] = useState(false);
  const [viewYear, setViewYear] = useState(selected?.year ?? today.getFullYear());
  const [viewMonth, setViewMonth] = useState(selected?.month ?? today.getMonth());
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const buttonRef = useRef<HTMLButtonElement>(null);
  const calendarRef = useRef<HTMLDivElement>(null);

  // Sync view to selected value when it changes externally
  useEffect(() => {
    if (selected) {
      setViewYear(selected.year);
      setViewMonth(selected.month);
    }
  }, [value]);

  // Position the dropdown when opening
  const openDropdown = () => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    let top: number;
    if (spaceBelow >= CALENDAR_HEIGHT + 8 || spaceBelow >= spaceAbove) {
      // Open downward
      top = rect.bottom + 4;
    } else {
      // Open upward
      top = rect.top - CALENDAR_HEIGHT - 4;
    }

    let left = rect.left;
    if (left + CALENDAR_WIDTH > window.innerWidth - 8) {
      left = rect.right - CALENDAR_WIDTH;
    }

    setDropdownStyle({ top, left, width: Math.max(rect.width, CALENDAR_WIDTH) });
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        buttonRef.current && !buttonRef.current.contains(e.target as Node) &&
        calendarRef.current && !calendarRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  };

  const handleDayClick = (day: number) => {
    const key = toDateKey(viewYear, viewMonth, day);
    if (min && key < min) return;
    onChange(key);
    setOpen(false);
  };

  const totalDays = daysInMonth(viewYear, viewMonth);
  const startOffset = firstWeekday(viewYear, viewMonth);
  const cells: (number | null)[] = [
    ...Array(startOffset).fill(null),
    ...Array.from({ length: totalDays }, (_, i) => i + 1),
  ];
  while (cells.length % 7 !== 0) cells.push(null);

  const calendar = open ? createPortal(
    <div
      ref={calendarRef}
      className="fixed z-[600] bg-[var(--surface-elevated)] border border-[var(--border-primary)] rounded-lg animate-slideDown"
      style={{ ...dropdownStyle, boxShadow: 'var(--shadow-lg)' }}
      onMouseDown={e => e.stopPropagation()}
    >
      {/* Month navigation */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-[var(--border-secondary)]">
        <button
          type="button"
          onClick={prevMonth}
          className="h-6 w-6 flex items-center justify-center rounded hover:bg-[var(--sidebar-hover)] transition-colors"
        >
          <ChevronLeft className="w-3.5 h-3.5 text-[var(--content-secondary)]" />
        </button>
        <span className="text-xs font-semibold text-[var(--content-primary)]">
          {MONTHS[viewMonth]} {viewYear}
        </span>
        <button
          type="button"
          onClick={nextMonth}
          className="h-6 w-6 flex items-center justify-center rounded hover:bg-[var(--sidebar-hover)] transition-colors"
        >
          <ChevronRight className="w-3.5 h-3.5 text-[var(--content-secondary)]" />
        </button>
      </div>

      {/* Day headers */}
      <div className="grid grid-cols-7 px-2 pt-2">
        {DAYS.map(d => (
          <div key={d} className="h-7 flex items-center justify-center text-[10px] font-medium text-[var(--content-tertiary)]">
            {d}
          </div>
        ))}
      </div>

      {/* Day grid */}
      <div className="grid grid-cols-7 px-2 pb-2">
        {cells.map((day, i) => {
          if (!day) return <div key={i} />;
          const key = toDateKey(viewYear, viewMonth, day);
          const isSelected = key === value;
          const isToday = key === todayKey;
          const isDisabled = !!min && key < min;

          return (
            <button
              key={i}
              type="button"
              onClick={() => handleDayClick(day)}
              disabled={isDisabled}
              className={`h-7 w-7 mx-auto flex items-center justify-center rounded-lg text-xs transition-colors ${
                isSelected
                  ? 'bg-[var(--accent-primary)] text-[var(--content-inverted)] font-semibold'
                  : isToday
                  ? 'bg-[var(--accent-subtle)] text-[var(--accent-primary)] font-semibold'
                  : isDisabled
                  ? 'text-[var(--content-tertiary)] opacity-40 cursor-not-allowed'
                  : 'text-[var(--content-primary)] hover:bg-[var(--sidebar-hover)]'
              }`}
            >
              {day}
            </button>
          );
        })}
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <div className={className} data-no-drag>
      <button
        ref={buttonRef}
        type="button"
        onClick={openDropdown}
        className="w-full px-2.5 py-1.5 bg-[var(--input-bg)] text-[var(--content-primary)] rounded-lg focus:outline-none focus:ring-1 focus:ring-[var(--accent-primary)] text-xs text-left"
      >
        {value ? formatDisplay(value) : <span className="text-[var(--content-tertiary)]">{placeholder}</span>}
      </button>
      {calendar}
    </div>
  );
};

export default DatePicker;
